// In-chat invite card ("Know someone who'd like this?") — pure helpers shared by the chat state, the
// card UI (MessageRow/InviteCard) and its tests. The wire shape is the harness contract: an owner's
// reply at a good moment carries `metadata.inviteCard` with the owner's own invite link, and the card
// rides the reply the way `creditsCard` does.
//
// Deliberately dependency-free (mirrors lib/credits-card.ts): unit tests must not drag the common-lib
// graph, so the validation here MIRRORS common-lib's `readInviteCard` rather than importing it. Keep the
// two in step.

/**
 * `earn` names the referral reward; `recommend` is the account's once-ever ask, with no reward wording.
 * An unrecognized or missing value reads as `earn`: an unknown variant means a newer server, and the
 * card we DO have is better than nothing at a moment the server judged worth the ask.
 */
export type InviteCardVariant = 'earn' | 'recommend';

export type InviteCardData = {
  /** Exactly the link the Share screen shares — the attributed one. */
  link: string;
  variant: InviteCardVariant;
  /** The REFERRER reward triple. All three or none: reward copy names every part. Never on `recommend`. */
  credits?: number;
  subAgentRuns?: number;
  agentMeetings?: number;
};

const positiveInt = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
};

/**
 * Read + sanitize `metadata.inviteCard`. Fail-closed on the link: without an https link there is no card,
 * because a Share button with nothing attributable behind it credits nobody.
 */
export function readInviteCard(metadata: unknown): InviteCardData | undefined {
  const raw = (metadata as { inviteCard?: unknown } | null | undefined)?.inviteCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const card = raw as Record<string, unknown>;
  const link = typeof card.link === 'string' ? card.link.trim() : '';
  let protocol = '';
  try {
    protocol = link ? new URL(link).protocol : '';
  } catch {
    protocol = '';
  }
  if (protocol !== 'https:') return undefined;

  const variant: InviteCardVariant = card.variant === 'recommend' ? 'recommend' : 'earn';
  const credits = positiveInt(card.credits);
  const subAgentRuns = positiveInt(card.subAgentRuns);
  const agentMeetings = positiveInt(card.agentMeetings);
  const reward = variant === 'earn' && credits !== undefined && subAgentRuns !== undefined && agentMeetings !== undefined;
  return { link, variant, ...(reward ? { credits, subAgentRuns, agentMeetings } : {}) };
}

/** The catalog keys + params the card renders, chosen here so the copy rule is testable without a DOM. */
export function inviteCardCopy(card: InviteCardData): {
  titleKey: string;
  bodyKey: string;
  bodyParams?: { credits: number; subAgentRuns: number; agentMeetings: number };
} {
  if (card.variant === 'recommend') return { titleKey: 'chat.inviteCard.recommend.title', bodyKey: 'chat.inviteCard.recommend.body' };
  if (card.credits !== undefined && card.subAgentRuns !== undefined && card.agentMeetings !== undefined) {
    return {
      titleKey: 'chat.inviteCard.earn.title',
      bodyKey: 'chat.inviteCard.earn.body',
      bodyParams: { credits: card.credits, subAgentRuns: card.subAgentRuns, agentMeetings: card.agentMeetings },
    };
  }
  return { titleKey: 'chat.inviteCard.earn.title', bodyKey: 'chat.inviteCard.earn.bodyNoAmount' };
}
